import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { LocalStoreService } from './local-store.service';

const WELCOME_PENDING_KEY = 'racebet_welcome_pending';

// Coordinates the one-time post-registration welcome modal.
//
// Producers (login-popup one-click / register-popup) call markPending() the
// moment an account is created. The consumer (main-layout) shows the modal
// when ALL of these hold:
//   - a pending flag exists (localStorage — survives a page reload),
//   - no auth popup is currently open (suppression counter — the one-click
//     credentials screen must never be covered),
//   - the freshly-registered user resolved as clientRegistered + TND.
//
// The flag is consumed exactly once as soon as the user's role is known —
// including for INELIGIBLE users (non-TND) — so it can never leak to a later
// session or a different account on the same browser.
// A pending flag older than this is discarded unshown — "right after
// registration" must not become "on some later visit".
const PENDING_MAX_AGE_MS = 15 * 60 * 1000;

export interface WelcomeCredentials {
  username: string;
  password: string;
}

@Injectable({ providedIn: 'root' })
export class WelcomeModalService {
  private suppressCount = 0;
  // One-click registrations carry the generated credentials into the welcome
  // modal (credentials mode). IN-MEMORY ONLY — never persisted: after a page
  // reload they are gone by design (the normal-registration path reloads and
  // needs no credentials; one-click does not reload).
  private pendingCredentials: WelcomeCredentials | null = null;

  // Emits whenever a suppressing popup closes — the consumer re-evaluates.
  readonly nudge$ = new Subject<void>();

  constructor(private localStore: LocalStoreService) {}

  markPending(credentials: WelcomeCredentials | null = null): void {
    this.localStore.setItem(WELCOME_PENDING_KEY, Date.now());
    this.pendingCredentials = credentials;
  }

  consumeCredentials(): WelcomeCredentials | null {
    const credentials = this.pendingCredentials;
    this.pendingCredentials = null;
    return credentials;
  }

  hasPending(): boolean {
    const markedAt = this.localStore.getItem<number>(WELCOME_PENDING_KEY);
    if (!markedAt) {
      return false;
    }
    if (Date.now() - markedAt > PENDING_MAX_AGE_MS) {
      this.clearPending();
      return false;
    }
    return true;
  }

  clearPending(): void {
    this.localStore.removeItem(WELCOME_PENDING_KEY);
  }

  // Auth popups (login / register) suppress the welcome modal while open.
  suppress(): void {
    this.suppressCount++;
  }

  release(): void {
    this.suppressCount = Math.max(0, this.suppressCount - 1);
    this.nudge$.next();
  }

  get suppressed(): boolean {
    return this.suppressCount > 0;
  }
}
